import { Drawer } from 'antd'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
} from 'react'

import { useAuctionDetailDrawerQuery } from '../../../graphql/generated'
import {
  DRAWER_TYPE,
  DRAWER_VIEW,
  resetAuctionDrawer,
  resetDrawerView,
} from '../../../redux/drawerSlice'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { propertyDrawerWrapperStyle } from '../../utils/layoutConst'
import { useAuctionActivityLog } from '../../utils/useAuctionActivityLog'
import { useAuctionLabelIds } from '../../utils/useAuctionLabelIds'
import { AuctionDetailDrawerBody } from '../AuctionDetailDrawerBody/AuctionDetailDrawerBody'
import { AuctionDetailDrawerFooter } from '../AuctionDetailDrawerFooter/AuctionDetailDrawerFooter'
import { AuctionDrawerPropertyHeader } from '../AuctionDrawerPropertyHeader/AuctionDrawerPropertyHeader'

export interface AuctionDetailDrawerProps {
  allowAssignmentMode?: boolean
}

const assignmentModeStyle = {
  rootClassName: '!z-[1100]',
  styles: {
    wrapper: {
      maxWidth: '75%',
      width: '400px',
      zIndex: 1900,
    },
  },
  closable: false,
  mask: false,
}

export const AuctionDetailDrawer: FunctionComponent<AuctionDetailDrawerProps> =
  props => {
    const dispatch = useAppDispatch()
    const labelIds = useAuctionLabelIds()

    const { userId, tenantId } = useAppSelector(state => state.myProfile)
    const drawerId = useAppSelector(
      state => state.drawer?.[DRAWER_TYPE.AUCTION]?.id
    )
    const drawerProps = useAppSelector(
      state => state.drawer?.viewType === DRAWER_VIEW.ASSIGNMENT_MODE
    )
      ? assignmentModeStyle
      : propertyDrawerWrapperStyle

    const handleClose = useCallback(() => {
      dispatch(resetAuctionDrawer())
      dispatch(resetDrawerView())
    }, [dispatch])

    useEffect(() => {
      return () => {
        if (drawerId) handleClose()
      }
    }, [drawerId, handleClose])

    const [dataSource] = useAuctionDetailDrawerQuery({
      variables: {
        id: drawerId || '',
        accountUserId: userId,
        accountId: tenantId,
        labelIds: labelIds.data,
      },
      pause: !drawerId,
    })

    const auction = useMemo(
      () => dataSource.data?.auctionListingById,
      [dataSource.data?.auctionListingById]
    )

    useAuctionActivityLog(auction)

    const linkedPropertiesIds = useMemo(
      () =>
        auction?.linkedProperties.nodes?.flatMap(item =>
          item?.propertyId !== undefined ? [item.propertyId] : []
        ) || [],
      [auction?.linkedProperties.nodes]
    )

    return (
      <Drawer
        title={<AuctionDrawerPropertyHeader auctionUrl={auction?.detailUrl} />}
        open={!!drawerId}
        onClose={handleClose}
        footer={
          props.allowAssignmentMode && (
            <AuctionDetailDrawerFooter
              auctionId={auction?.id}
              linkedPropertiesIds={linkedPropertiesIds}
            />
          )
        }
        {...drawerProps}
      >
        <AuctionDetailDrawerBody dataSource={dataSource} />
      </Drawer>
    )
  }
